/**
 * Upper bound on how many `VideoPreview` clips stay "warm" (source kept loaded
 * and buffered for instant, request-free replay) at once. Kept small so a large
 * grid the user plays through doesn't accumulate unbounded buffered `<video>`
 * elements. Change this single constant to tune the memory/instant-replay trade.
 */
export const MAX_WARM_VIDEO_PREVIEWS = 10;

type WarmEntry = {
	/** Cool this preview down (drops its buffered source so it can be evicted). */
	release: () => void;
};

/**
 * Insertion-ordered map acting as an LRU: the first key is the least-recently
 * used warm preview, the last is the most-recently used. Re-inserting a key
 * moves it to the most-recently-used position.
 */
const warmRegistry = new Map<number, WarmEntry>();
let nextWarmSlotId = 0;

/**
 * Per-`VideoPreview` handle into the shared warm-preview LRU. Coordinates the
 * global cap so at most {@link MAX_WARM_VIDEO_PREVIEWS} clips stay buffered.
 */
export type WarmVideoSlot = {
	/**
	 * Register this preview as warm (or bump it to most-recently-used). Evicts
	 * the least-recently-used warm preview when the cap is exceeded.
	 */
	markWarm: (release: () => void) => void;
	/** Drop this preview from the registry (went cold, source changed, cleanup). */
	remove: () => void;
};

export function createWarmVideoSlot(): WarmVideoSlot {
	const id = nextWarmSlotId++;

	const markWarm = (release: () => void) => {
		// Delete-then-set moves this slot to the most-recently-used position.
		warmRegistry.delete(id);
		warmRegistry.set(id, { release });

		while (warmRegistry.size > MAX_WARM_VIDEO_PREVIEWS) {
			const oldestId = warmRegistry.keys().next().value;
			// Never evict ourselves — we're the freshest entry we just inserted.
			if (oldestId === undefined || oldestId === id) {
				break;
			}
			const oldest = warmRegistry.get(oldestId);
			warmRegistry.delete(oldestId);
			oldest?.release();
		}
	};

	const remove = () => {
		warmRegistry.delete(id);
	};

	return { markWarm, remove };
}
